import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CATEGORY_COLORS } from "../lib/constants";
import { trackPageview } from "../lib/api";
import { useT } from "../lib/LangContext";
import { buildGoogleTranslateUrl } from "./GoogleTranslateLink";

export const HeroSection = ({ article }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { t, tCategory, timeAgo, lang } = useT();
  if (!article) return null;
  const color = CATEGORY_COLORS[article.category] || "#0AADA0";
  const hasImage = !!article.image_url && !imgFailed;

  return (
    <section
      className="relative w-full overflow-hidden rounded-xl mb-8 fade-up"
      style={{ minHeight: 420 }}
      data-testid="featured-hero"
    >
      {hasImage ? (
        <img
          src={article.image_url}
          alt={article.title}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: color }}
          data-testid="featured-hero-fallback"
        >
          <span className="font-serif-display italic text-white text-5xl">
            {tCategory(article.category)}
          </span>
        </div>
      )}
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.45)" }} />

      <div className="relative p-6 sm:p-10 lg:p-12 flex flex-col justify-end min-h-[420px]">
        <div className="mb-4 flex items-center gap-3">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] uppercase tracking-wider text-white"
            style={{ background: color }}
            data-testid="hero-category-badge"
          >
            {tCategory(article.category)}
          </span>
          <span className="text-xs text-white/85 uppercase tracking-wider">
            {t("featured")} · {timeAgo(article.published_at)}
          </span>
        </div>

        <h1
          className="font-serif-display text-3xl sm:text-4xl lg:text-[44px] leading-tight text-white mb-3 max-w-3xl"
          data-testid="featured-title"
        >
          {article.title}
        </h1>

        <p className="text-[15px] text-white/85 leading-relaxed max-w-2xl mb-6 line-clamp-3">
          {article.ai_summary}
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          <Link
            to={`/article/${article.id}${article.slug ? `-${article.slug}` : ""}`}
            onClick={() => trackPageview("/article", article.id)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm transition-colors"
            data-testid="featured-read-btn"
          >
            {t("read_story")} <ArrowRight size={16} />
          </Link>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/90 hover:text-white underline underline-offset-2"
            data-testid="featured-source-link"
          >
            {t("read_full_article_cta")} →
          </a>
        </div>
        <p
          className="text-[10px] text-white/70 mt-2"
          data-testid="featured-gt-attr"
        >
          {t("translated_via_google")} {article.source_name}
        </p>
      </div>
    </section>
  );
};
